/**
 * Pasted text and FAQ lists (owner, 2026-10-21: each is a document of its own, edited by opening it again and saved
 * through Replace): what the dashboard saves them as, and reads them back from. A text is saved as it is written,
 * Markdown; an FAQ list as a two-column table under "| Question | Answer |", so the worker's table rule makes each
 * question a passage of its own with its answer (4 questions or more; fewer share one passage). Both are .md files,
 * so the upload path, the plan's limits and Replace are a file's own. Keep in step with the Angular dashboard's
 * `features/knowledge-base/kb-text.ts`.
 */
import type { KnowledgeBaseFile } from "@/services/knowledge-base.service";
import { NAME_MAX_CHARS } from "./kb-upload-rules";

export interface FaqEntry {
  question: string;
  answer: string;
}

export const TEXT_EXTENSION = ".md";
/** A name, its .md included, within what a file name may be. */
export const TITLE_MAX_CHARS = NAME_MAX_CHARS - TEXT_EXTENSION.length;

const FAQ_HEADER = "| Question | Answer |";
const FAQ_DELIMITER = "| --- | --- |";
/** The line between two cells: a pipe inside a cell is saved as "\|", so it never reads as one. */
const CELL_BREAK = " | ";

/** The document a text is saved as: its name ending in .md, and its words. */
export const textFileName = (title: string) => `${title.trim()}${TEXT_EXTENSION}`;

/** What Edit offers the name as: the file's name without its .md. */
export const titleOf = (fileName: string) =>
  fileName.toLowerCase().endsWith(TEXT_EXTENSION) ? fileName.slice(0, -TEXT_EXTENSION.length) : fileName;

/** A text file Edit can open: Markdown or plain text, ready. */
export const isEditableText = (file: Pick<KnowledgeBaseFile, "file_type" | "status">) =>
  file.status === "ready" && (file.file_type === "text/markdown" || file.file_type === "text/plain");

/** One table cell: on one line, its pipes escaped. */
const cell = (text: string) => text.replace(/\s*\n\s*/g, " ").trim().replace(/\|/g, "\\|");

/** An FAQ list as the table it is saved as. */
export function faqMarkdown(entries: readonly FaqEntry[]): string {
  return `${[FAQ_HEADER, FAQ_DELIMITER, ...entries.map((entry) => `| ${cell(entry.question)} | ${cell(entry.answer)} |`)].join("\n")}\n`;
}

/** The entries of an FAQ list this dashboard saved, or null for any other text (which Edit opens as text). */
export function parseFaq(markdown: string): FaqEntry[] | null {
  const [header, delimiter, ...rows] = markdown.replace(/\r\n?/g, "\n").trim().split("\n");
  if (header !== FAQ_HEADER || delimiter !== FAQ_DELIMITER) return null;
  const entries: FaqEntry[] = [];
  for (const row of rows) {
    const cells = row.startsWith("| ") && row.endsWith(" |") ? row.slice(2, -2).split(CELL_BREAK) : [];
    if (cells.length !== 2) return null;
    const [question, answer] = cells.map((text) => text.replace(/\\\|/g, "|"));
    entries.push({ question, answer });
  }
  return entries;
}

/** What is missing before a text can be saved, in the dialog's words; null when it can. */
export function textError(title: string, body: string): string | null {
  if (!title.trim()) return "Give it a name.";
  if (title.trim().length > TITLE_MAX_CHARS) return `Keep the name to ${TITLE_MAX_CHARS} characters.`;
  return body.trim() ? null : "Write the text first.";
}

/** The same for an FAQ list: a name, and every row a question with its answer. */
export function faqError(title: string, entries: readonly FaqEntry[]): string | null {
  const named = textError(title, "x");
  if (named) return named;
  if (!entries.some((entry) => entry.question.trim() || entry.answer.trim())) return "Add a question and its answer.";
  return entries.some((entry) => !entry.question.trim() !== !entry.answer.trim())
    ? "Every question needs its answer, and every answer its question."
    : null;
}

/** The rows worth saving: an empty one (both cells blank) is left out. */
export const filledEntries = (entries: readonly FaqEntry[]) =>
  entries.filter((entry) => entry.question.trim() && entry.answer.trim());
